import { ArrowRight } from "lucide-react";
import { cn } from "web-design-system";
import {
  AllocationBucket,
  PortfolioReview,
  PortfolioReviewReport,
} from "creso-core/types";
import { formatAsCrore, formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import {
  JourneyMetricsTable,
  ReturnsChart,
  SignedPercent,
} from "@/modules/portfolio-review/components/portfolio-journey";
import {
  LegendItem,
  mergeBuckets,
} from "@/modules/portfolio-review/components/workbench/live-insights";
import { ReportPageTitle, ReportStats, SignedReturn } from "./report-blocks";
import {
  RankedItem,
  bucketColor,
  positionsBefore,
  topAmcs,
  topFunds,
} from "./report-data";

const formatPct = (value: number | string) => formatAsPercentage(value, "-", 0);

export function PerformancePage({ report }: { report: PortfolioReviewReport }) {
  const { overview } = report;
  const { backtest } = overview;
  const invested = Number(overview.amount_invested ?? 0);
  const current = Number(overview.current_value ?? 0);
  const gain = current - invested;
  const { current: currentMetrics, benchmark: benchmarkMetrics } =
    backtest.metrics;
  const returnsOf = (m: NonNullable<typeof currentMetrics>) =>
    formatAsCrore(
      Number(m.final_value) +
        Number(m.total_withdrawn) -
        Number(m.total_invested)
    );

  return (
    <div className="flex flex-col gap-6">
      <ReportStats
        stats={[
          { label: "Current value", value: formatAsCrore(current) },
          { label: "Invested", value: formatAsCrore(invested) },
          {
            label: "Returns",
            value: (
              <span className="flex items-baseline gap-1">
                {formatAsCrore(gain)}
                {invested ? (
                  <span className="text-xs font-normal">
                    <SignedReturn value={(gain / invested) * 100} />
                  </span>
                ) : null}
              </span>
            ),
          },
          {
            label: "XIRR",
            value: overview.xirr ? (
              <SignedReturn value={Number(overview.xirr)} />
            ) : (
              "-"
            ),
          },
        ]}
      />
      <div className="flex flex-col gap-2">
        <ReportPageTitle title="Returns Backtest" />
        <ReturnsChart data={backtest} grid={false} className="h-64" />
      </div>
      {currentMetrics && benchmarkMetrics ? (
        <JourneyMetricsTable
          benchmarkName={backtest.benchmark_name}
          caption="For the same investment"
          markers
          rows={[
            {
              label: "Final value",
              current: formatAsCrore(currentMetrics.final_value),
              benchmark: formatAsCrore(benchmarkMetrics.final_value),
            },
            {
              label: "Returns",
              current: returnsOf(currentMetrics),
              benchmark: returnsOf(benchmarkMetrics),
            },
            {
              label: "XIRR",
              current: <SignedPercent value={currentMetrics.xirr} />,
              benchmark: <SignedPercent value={benchmarkMetrics.xirr} />,
            },
          ]}
        />
      ) : null}
    </div>
  );
}

export function PortfolioOverviewPage({
  review,
  report,
  amcName,
}: {
  review: PortfolioReview;
  report: PortfolioReviewReport;
  amcName: (code: string | null) => string;
}) {
  const { before } = report.summary.exposure;
  const positions = positionsBefore(review.lines);
  const funds = topFunds(positions);
  const buckets = report.overview.asset_allocation.before;

  return (
    <div className="flex flex-col gap-6">
      <ReportPageTitle title="Portfolio Overview" />
      {buckets.length ? (
        <div className="flex flex-col gap-4 rounded-xl bg-card p-4">
          <span className="text-sm font-semibold">Asset Allocation</span>
          <StackedBar buckets={buckets} />
          <AllocationLegend buckets={buckets} />
        </div>
      ) : null}
      <div className="flex flex-col gap-4 rounded-xl bg-card p-4">
        <span className="text-sm font-semibold">
          {before.fund_count} funds from {before.amc_count} AMCs
        </span>
        <TopThreeRow
          label={`Top 3 AMCs: ${formatPct(before.top_3_amc_pct)}`}
          items={topAmcs(positions, amcName)}
        />
        <TopThreeRow
          label={`Top 3 Funds: ${formatPct(sumPct(funds))}`}
          items={funds}
        />
      </div>
    </div>
  );
}

// The first bucket leads on the left with its breakdown, the rest sit right.
function AllocationLegend({ buckets }: { buckets: AllocationBucket[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
      {buckets
        .filter((bucket) => bucket.pct > 0)
        .map((bucket, index) => (
          <span
            key={bucket.key}
            className={cn("flex items-center gap-2", index === 0 && "mr-auto")}
          >
            <span
              className="size-3 shrink-0 rounded-xs"
              style={{ background: bucketColor(bucket.key, index) }}
            />
            <span className="font-semibold">
              {bucket.label}: {formatPct(bucket.pct)}
            </span>
            {bucket.key.toLowerCase() === "indian_equity" &&
            bucket.sub_buckets?.length ? (
              <span className="ml-2 flex gap-3">
                (
                {bucket.sub_buckets.map((sub) => (
                  <span key={sub.key}>
                    {sub.label}: {formatPct(sub.pct)}
                  </span>
                ))}
                )
              </span>
            ) : null}
          </span>
        ))}
    </div>
  );
}

function TopThreeRow({ label, items }: { label: string; items: RankedItem[] }) {
  const max = Math.max(...items.map((item) => item.pct), 1);
  return (
    <div className="flex items-center gap-4 rounded-xl bg-secondary p-2">
      <span className="w-32 shrink-0 pl-2 text-xs font-semibold">{label}</span>
      <div className="grid flex-1 grid-cols-3 gap-2">
        {items.map((item) => (
          <div
            key={item.key}
            className="flex items-center gap-2 rounded-lg bg-background p-2"
          >
            {/* A bar behind the name, sized to the item's share. */}
            <div className="relative flex min-w-0 flex-1 items-center gap-2">
              <div
                className="absolute inset-y-0 left-0 min-w-10 rounded-lg bg-tag-blue"
                style={{ width: `${(item.pct / max) * 80}%` }}
              />
              <span className="relative">
                <AmcLogo amc_code={item.amcCode} />
              </span>
              <span className="relative flex-1 truncate text-xs">
                {item.name}
              </span>
            </div>
            <span className="pr-2 text-sm font-semibold">
              {formatPct(item.pct)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AssetAllocationPage({
  report,
}: {
  report: PortfolioReviewReport;
}) {
  const { before, after } = report.overview.asset_allocation;
  const buckets = mergeBuckets(before, after);

  return (
    <div className="flex flex-col gap-6">
      <ReportPageTitle title="Asset allocation" />
      <div className="flex items-center gap-6 text-sm text-muted-foreground">
        <LegendItem>Current portfolio</LegendItem>
        <LegendItem dashed>After review</LegendItem>
      </div>
      <div className="flex flex-col gap-8">
        <StackedBar buckets={before} />
        <StackedBar buckets={after} dashed />
      </div>
      <div className="grid grid-cols-4 gap-3">
        {buckets.map((bucket, index) => {
          const color = bucketColor(bucket.key, index);
          const merged = mergeBuckets(
            before.find((b) => b.key === bucket.key)?.sub_buckets ?? [],
            after.find((b) => b.key === bucket.key)?.sub_buckets ?? []
          );
          const subs =
            bucket.key.toLowerCase() === "global"
              ? groupTail(merged, TOP_COUNTRIES)
              : merged;
          // Sub-bucket shares are of the whole portfolio, so size their bars
          // against the bucket's own share to keep them readable.
          const scale = Math.max(bucket.before, bucket.after, 1);
          const subColor = `color-mix(in srgb, ${color} 55%, transparent)`;
          return (
            <div key={bucket.key} className="flex flex-col gap-4">
              <div className="flex items-center gap-2 rounded-lg bg-card p-4 text-sm">
                <span
                  className="size-3 shrink-0 rounded-xs"
                  style={{ background: color }}
                />
                <span className="flex-1 truncate">{bucket.label}</span>
                <span className="flex items-center gap-1">
                  {formatPct(bucket.before)}
                  <ArrowRight className="size-3" />
                  {formatPct(bucket.after)}
                </span>
              </div>
              {subs.map((sub) => (
                <div key={sub.key} className="flex flex-col gap-1 px-4">
                  <span className="text-xs">{sub.label}</span>
                  <SubBar
                    pct={sub.before}
                    width={(sub.before / scale) * 100}
                    color={subColor}
                  />
                  <SubBar
                    pct={sub.after}
                    width={(sub.after / scale) * 100}
                    color={subColor}
                    dashed
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StackedBar({
  buckets,
  dashed,
}: {
  buckets: AllocationBucket[];
  dashed?: boolean;
}) {
  return (
    <div className={cn("flex w-full gap-2", dashed ? "h-5" : "h-4")}>
      {buckets
        .filter((bucket) => bucket.pct > 0)
        .map((bucket, index) => {
          const color = bucketColor(bucket.key, index);
          return dashed ? (
            // Dots sit on a pale track of the bucket's colour.
            <div
              key={bucket.key}
              className="flex h-full items-center rounded-full px-1.5"
              style={{
                width: `${bucket.pct}%`,
                background: `color-mix(in srgb, ${color} 15%, transparent)`,
              }}
            >
              <div
                className="h-2 w-full rounded-full"
                style={{ background: dots(color, 8) }}
              />
            </div>
          ) : (
            <div
              key={bucket.key}
              className="h-full rounded-full"
              style={{ width: `${bucket.pct}%`, background: color }}
            />
          );
        })}
    </div>
  );
}

// Solid bar for the current portfolio, dotted for after the review.
function SubBar({
  pct,
  width,
  color,
  dashed,
}: {
  pct: number;
  width: number;
  color: string;
  dashed?: boolean;
}) {
  return (
    <div className="flex h-5 items-center gap-4">
      <div className="flex-1">
        <div
          className="h-1.5 rounded-full"
          style={{
            width: `${Math.min(Math.max(width, 0), 100)}%`,
            background: dashed ? dots(color, 6) : color,
          }}
        />
      </div>
      <span className="w-12 text-right text-xs">
        {formatAsPercentage(pct, "-", 1)}
      </span>
    </div>
  );
}

// Square dots the height of the bar, with half-size gaps between them.
const dots = (color: string, size: number) =>
  `repeating-linear-gradient(90deg, ${color} 0 ${size}px, transparent ${size}px ${size * 1.5}px)`;

const TOP_COUNTRIES = 4;

type MergedBucket = ReturnType<typeof mergeBuckets>[number];

// Keeps the largest `count` entries (by either side) and sums the rest into
// "Others", so before and after always show the same rows.
function groupTail(subs: MergedBucket[], count: number): MergedBucket[] {
  if (subs.length <= count) return subs;
  const sorted = [...subs].sort(
    (a, b) => Math.max(b.before, b.after) - Math.max(a.before, a.after)
  );
  const rest = sorted.slice(count);
  return [
    ...sorted.slice(0, count),
    {
      key: "others",
      label: "Others",
      before: rest.reduce((sum, sub) => sum + sub.before, 0),
      after: rest.reduce((sum, sub) => sum + sub.after, 0),
    },
  ];
}

const sumPct = (items: RankedItem[]) =>
  items.reduce((sum, item) => sum + item.pct, 0);
